import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Glasses, Box, Layers, ArrowRight, CheckCircle2 } from "lucide-react";

// AI Generated Visual Previews for XR
import arPreviewImg from "../assets/images/ar_preview.jpg";
import vrPreviewImg from "../assets/images/vr_preview.jpg";
import mrPreviewImg from "../assets/images/mr_preview.jpg";

const xrServices = [
  {
    tag: "Mobile & Web AR",
    title: "Augmented Reality (AR)",
    subtitle: "Real-world visual enhancement with 3D digital overlays",
    image: arPreviewImg,
    description:
      "Engage your audience with browser-based WebAR and mobile experiences. Allow customers to preview 3D products, explore interactive models, and visualize physical scale directly through their camera.",
    features: [
      "WebAR & App-Free Mobile Experiences",
      "Interactive 3D Product Visualizers",
      "Virtual Try-Ons & Social Brand Filters",
      "Industrial Field & Guided Overlays",
    ],
    color: "#27B8FF", // Neon Cyan
    icon: Layers,
    badge: "Interactive Overlay",
  },
  {
    tag: "Fully Immersive VR",
    title: "Virtual Reality (VR)",
    subtitle: "Complete digital immersion for training & simulation",
    image: vrPreviewImg,
    description:
      "Step into custom, photorealistic 3D virtual worlds. We build high-fidelity VR simulations for enterprise safety, complex procedural training, and interactive metaverse walkthroughs.",
    features: [
      "Enterprise & Industrial Safety Simulators",
      "Architectural & Real Estate Virtual Walkthroughs",
      "Meta Quest & PC-VR Application Development",
      "Collaborative Multi-User Virtual Workspaces",
    ],
    color: "#7C4DFF", // AI Purple Glow
    icon: Glasses,
    badge: "Full Immersion",
  },
  {
    tag: "Spatial Computing",
    title: "Mixed Reality (MR / XR)",
    subtitle: "Seamless fusion of digital holograms & physical spaces",
    image: mrPreviewImg,
    description:
      "Pioneering next-generation spatial computing for Apple Vision Pro and HoloLens. Virtual holograms anchor to real-world physics, allowing natural gestures and eye-tracking interactions.",
    features: [
      "Apple Vision Pro & Meta Quest 3 Mixed Mode",
      "Interactive Digital Twins for Manufacturing",
      "Physics-Aware Real-Time 3D Holograms",
      "Natural Hand-Tracking & Spatial Interfaces",
    ],
    color: "#EC4899", // Neon Pink
    icon: Box,
    badge: "Spatial Fusion",
  },
];

const XRSection = () => {
  return (
    <section className="section-padding relative overflow-hidden" id="xr-solutions">
      {/* Background Ambience & Lighting */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/2 left-1/4 -translate-y-1/2 w-96 h-96 rounded-full opacity-15 blur-[120px]"
          style={{ background: "radial-gradient(circle, #7C4DFF, transparent)" }}
        />
        <div
          className="absolute top-1/2 right-1/4 -translate-y-1/2 w-96 h-96 rounded-full opacity-15 blur-[120px]"
          style={{ background: "radial-gradient(circle, #27B8FF, transparent)" }}
        />
        <div className="absolute inset-0 tech-grid opacity-10" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-10"
        >
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight"
            style={{ fontFamily: '"Space Grotesk", sans-serif' }}
          >
            Extended Reality <span className="gradient-text">(XR) Solutions</span>
          </h2>
          <p className="text-slate-400 max-w-3xl mx-auto text-sm sm:text-base leading-relaxed">
            Transform how users interact with your brand. We engineer end-to-end Augmented Reality (AR),
            Virtual Reality (VR), and Mixed Reality (MR) ecosystems tailored for modern enterprises.
          </p>
        </motion.div>

        {/* 3 Interactive Solution Cards with AI Generated Previews */}
        <div className="grid md:grid-cols-3 gap-8">
          {xrServices.map((item, idx) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.15 }}
                whileHover={{ y: -8 }}
                className="rounded-2xl p-6 flex flex-col justify-between group transition-all duration-300 overflow-hidden"
                style={{
                  background: "linear-gradient(180deg, rgba(14, 22, 54, 0.6) 0%, rgba(10, 16, 41, 0.8) 100%)",
                  backdropFilter: "blur(16px)",
                  border: "1px solid rgba(124, 77, 255, 0.18)",
                  boxShadow: "0 12px 36px -10px rgba(0, 0, 0, 0.6)",
                }}
              >
                <div>
                  {/* AI Generated Preview Image Banner */}
                  <div className="relative w-full h-48 rounded-xl overflow-hidden mb-6 border border-white/10 group-hover:border-ai-glow/40 transition-all duration-300">
                    <img
                      src={item.image}
                      alt={`${item.title} preview`}
                      className="w-full h-full object-cover transform group-hover:scale-108 transition-transform duration-700 ease-out"
                      loading="lazy"
                    />
                    {/* Dark gradient fade over bottom of image */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1029]/90 via-transparent to-black/20" />

                    {/* Floating Icon */}
                    <div
                      className="absolute top-3 left-3 w-10 h-10 rounded-lg flex items-center justify-center backdrop-blur-md shadow-lg"
                      style={{
                        background: "rgba(10, 16, 41, 0.85)",
                        border: `1px solid ${item.color}60`,
                      }}
                    >
                      <Icon className="w-5 h-5" style={{ color: item.color }} />
                    </div>

                    {/* Floating Badge */}
                    <span
                      className="absolute top-3 right-3 text-[11px] font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider backdrop-blur-md shadow-lg"
                      style={{
                        background: "rgba(10, 16, 41, 0.85)",
                        color: item.color,
                        border: `1px solid ${item.color}50`,
                      }}
                    >
                      {item.badge}
                    </span>
                  </div>

                  {/* Title & Subtitle */}
                  <h3 className="text-xl font-bold text-white mb-1.5 group-hover:text-ai-glow transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-xs font-medium mb-3 text-slate-300">
                    {item.subtitle}
                  </p>
                  <p className="text-sm text-slate-400 leading-relaxed mb-6">
                    {item.description}
                  </p>

                  {/* Feature Checkpoints */}
                  <ul className="space-y-2.5 mb-8">
                    {item.features.map((feat, fIdx) => (
                      <li key={fIdx} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <CheckCircle2
                          className="w-4 h-4 mt-0.5 flex-shrink-0"
                          style={{ color: item.color }}
                        />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Card Footer Link */}
                <Link
                  to="/contact"
                  className="pt-4 border-t border-slate-800/80 flex items-center justify-between text-sm font-semibold text-slate-300 group-hover:text-white transition-colors"
                >
                  <span>Request XR Consultation</span>
                  <ArrowRight
                    className="w-4 h-4 transform group-hover:translate-x-1.5 transition-transform"
                    style={{ color: item.color }}
                  />
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>

      <div className="section-divider mt-6 sm:mt-8" />
    </section>
  );
};

export default XRSection;
